import { useForm } from '@tanstack/react-form'
import type { ClientResponseDto } from '@trellis/api'
import { EmailInput } from '@trellis/roots/forms'
import { Input } from '@trellis/roots/input'
import { Label } from '@trellis/roots/label'
import { FormDialog } from '@trellis/roots/overlays'
import { Textarea } from '@trellis/roots/textarea'
import {
  CreateClientSchema,
  emptyToUndefined,
  optionalEmail,
  type CreateClient,
} from '@trellis/shared'
import { toast } from 'sonner'
import { z } from 'zod'

import { PhoneFormField } from '@/components/phone-form-field'
import { useCreateClientMutation } from '@/hooks'

interface AddClientDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  defaultName: string
  onCreated: (client: ClientResponseDto) => void
}

export function AddClientDialog({
  open,
  onOpenChange,
  defaultName,
  onCreated,
}: AddClientDialogProps) {
  const createMutation = useCreateClientMutation()

  const form = useForm({
    defaultValues: {
      name: defaultName,
      email: '',
      phone: '',
      company: '',
      address: '',
    },
    onSubmit: async ({ value }) => {
      try {
        const payload: CreateClient = CreateClientSchema.parse({
          ...value,
          status: 'ACTIVE',
        })
        const result = await createMutation.mutateAsync(payload)
        toast.success('Client created')
        onCreated(result.data)
        onOpenChange(false)
      } catch (caughtError) {
        const message =
          caughtError instanceof Error ? caughtError.message : 'Failed to create client'
        toast.error('Failed to create client', { description: message })
      }
    },
  })

  return (
    <form.Subscribe selector={(state) => state.isSubmitting}>
      {(isSubmitting) => (
        <FormDialog
          open={open}
          onOpenChange={onOpenChange}
          title="New client"
          description="Quickly create a client without leaving this form."
          hideDescription
          submitLabel="Create client"
          pendingLabel="Creating..."
          isPending={isSubmitting}
          onSubmit={() => void form.handleSubmit()}
        >
          <form.Field
            name="name"
            validators={{ onSubmit: CreateClientSchema.shape.name }}
            children={(field) => (
              <div className="flex flex-col gap-1.5">
                <Label htmlFor={field.name}>Name</Label>
                <Input
                  id={field.name}
                  value={field.state.value}
                  onBlur={field.handleBlur}
                  onChange={(changeEvent) => field.handleChange(changeEvent.target.value)}
                  placeholder="Client Name"
                  autoFocus
                />
                {field.state.meta.errors.length > 0 ? (
                  <p className="text-destructive-text text-xs">
                    {field.state.meta.errors[0]?.message}
                  </p>
                ) : null}
              </div>
            )}
          />

          <form.Field
            name="email"
            validators={{ onSubmit: optionalEmail }}
            children={(field) => (
              <div className="flex flex-col gap-1.5">
                <Label htmlFor={field.name}>Email</Label>
                <EmailInput
                  id={field.name}
                  value={field.state.value}
                  onBlur={field.handleBlur}
                  onChange={(changeEvent) => field.handleChange(changeEvent.target.value)}
                  placeholder="client@example.com"
                />
                {field.state.meta.errors.length > 0 ? (
                  <p className="text-destructive-text text-xs">
                    {field.state.meta.errors[0]?.message}
                  </p>
                ) : null}
              </div>
            )}
          />

          <form.Field
            name="phone"
            children={(field) => (
              <div className="flex flex-col gap-1.5">
                <PhoneFormField
                  value={field.state.value}
                  onChange={field.handleChange}
                  onBlur={field.handleBlur}
                />
              </div>
            )}
          />

          <form.Field
            name="company"
            validators={{
              onSubmit: z
                .string()
                .trim()
                .transform(emptyToUndefined)
                .pipe(z.string().max(255).optional()),
            }}
            children={(field) => (
              <div className="flex flex-col gap-1.5">
                <Label htmlFor={field.name}>Company</Label>
                <Input
                  id={field.name}
                  value={field.state.value}
                  onBlur={field.handleBlur}
                  onChange={(changeEvent) => field.handleChange(changeEvent.target.value)}
                  placeholder="Company Name"
                />
                {field.state.meta.errors.length > 0 ? (
                  <p className="text-destructive-text text-xs">
                    {field.state.meta.errors[0]?.message}
                  </p>
                ) : null}
              </div>
            )}
          />

          <form.Field
            name="address"
            children={(field) => (
              <div className="flex flex-col gap-1.5">
                <Label htmlFor={field.name}>Address</Label>
                <Textarea
                  id={field.name}
                  value={field.state.value}
                  onBlur={field.handleBlur}
                  onChange={(changeEvent) => field.handleChange(changeEvent.target.value)}
                  placeholder={'Level 3, 123 Collins Street\nMelbourne VIC 3000'}
                  rows={3}
                />
              </div>
            )}
          />
        </FormDialog>
      )}
    </form.Subscribe>
  )
}
